import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcessAsyncRecordStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

import {FixFailedRoutingAndMappingModalQuery} from './__generated/FixFailedRoutingAndMappingModalQuery';
import {isAsyncDataExecuteSchemaMapper} from './FixFailedRoutingAndMappingConstants';

type AsyncRecord = ExtractByTypename<
  FixFailedRoutingAndMappingModalQuery['processTask'],
  'ProcessTaskNode'
>['processAsyncRecords'][number];

export interface SelectErrorPaneProps {
  asyncRecords: Array<AsyncRecord>;
  onSelectError: (record: AsyncRecord) => void;
}

export default function SelectErrorPane(props: SelectErrorPaneProps): JSX.Element {
  const failedRecords = props.asyncRecords.filter(({status}) => status === ProcessAsyncRecordStatus.FAILED);

  return (
    <div className={css(styles.description)}>
      Fix the following {pluralize(failedRecords.length, 'error')} so the next task(s) or output(s) in the
      workflow can be created.
      <div className={css(styles.banners)} {...getTestProps({component: 'SelectErrorPane', element: 'root'})}>
        {failedRecords.length > 0 ? (
          <InlineBannerStack
            messages={failedRecords.map((record) => {
              let errorMessagePrefix = '';
              if (isAsyncDataExecuteSchemaMapper(record.asyncData)) {
                const toSchema = record.asyncData.schemaMapper.toSchema;
                errorMessagePrefix = `Mapping to ${assertIsAccessible(toSchema).name}: `;
              }
              // TODO(haroon): Add message prefix for routing errors.
              const text = `${errorMessagePrefix}${record.details ?? 'Mapping has failed unexpectedly'}`;
              return {
                text,
                bannerType: InlineBannerType.ERROR,
                action: {
                  actionText: 'Fix error',
                  actionType: InlineBannerActionType.CUSTOM,
                  actionOnClick: () => {
                    props.onSelectError(record);
                  },
                },
              };
            })}
          />
        ) : (
          <>The selected task does not have any mapping errors.</>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  description: {marginTop: 24, marginRight: 6},
  banners: {
    marginTop: 16,
    marginRight: 16,
  },
});
